// Adapted from davinci-elections apps/web/src/components/member/layout.tsx and layouts/PublicLayout.tsx @ 6e23f58
// (themselves mirrored from vocdoni-app src/elements/PublicLayout.tsx and src/components/Navbar/index.tsx).
import { Box, Button, chakra, Flex, Heading, Link as CLink, Stack, Text, type BoxProps } from '@chakra-ui/react'
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react'
import { LuCircleHelp, LuUser } from 'react-icons/lu'
import { useQuery } from '@tanstack/react-query'
import { Link as RouterLink, useLocation, useNavigationType } from 'react-router'
import { api } from '~/api/client'
import { keys } from '~/api/hooks'
import { MiniFooter } from '~/components/MiniFooter'
import { ThemeControl } from '~/components/ThemeControl'
import { OrgMark, SiteContainer, SkipLink } from '~/components/site'
import { useT } from '~/i18n/use-t'
import { ErrorAlert, useFriendlyError } from './feedback'
import { COMPACT, ROOMY, ROOMY_LOW, visuallyHidden } from '~/theme/media'
import type { StepKey } from '~/lib/steps'
import { ActionBarSlot } from './ActionBar'
import { useKeyboardInset } from './useKeyboardInset'
import type { FaqId } from './faq-ids'
import { BackContext, BackLink, StepContext } from './FlowStepper'
import { HelpSheet } from './HelpSheet'

export { ErrorAlert, useFriendlyError }

/** A `<form>` laid out as a vertical stack (accepts `gap`, `noValidate`, `onSubmit`…). */
export const FormStack = chakra('form', { base: { display: 'flex', flexDirection: 'column' } })

/** The page heading of a member screen: vocdoni-app's regular-weight title with a muted subtitle. */
export const PageTitle = ({ title, subtitle }: { title: ReactNode; subtitle?: ReactNode }) => (
  <Stack gap={2}>
    <Heading
      as='h1'
      textWrap='balance'
      overflowWrap='anywhere'
      css={{ fontSize: '2xl', lineHeight: '1.25', [ROOMY]: { fontSize: '1.75rem' } }}
    >
      {title}
    </Heading>
    {subtitle && <Text color='fg.muted'>{subtitle}</Text>}
  </Stack>
)

/**
 * A section of a member page (vocdoni-app DashboardBox: 8 px radius, 1 px border): a content surface raised off the
 * canvas with the surface shadow (R113). `frameFrom='md'`: no frame on a phone (a box inside the page costs a phone
 * 28 px of its width), the raised card from `md`, for a page whose whole body is the section.
 */
export const MemberSection = ({ frameFrom, ...props }: { frameFrom?: 'md' } & BoxProps) => (
  <Box
    borderWidth={frameFrom ? { base: 0, md: '1px' } : '1px'}
    borderColor='border'
    borderRadius='md'
    p={frameFrom ? { base: 0, md: 6 } : { base: 4, md: 6 }}
    bg={frameFrom ? { base: 'transparent', md: 'bg' } : 'bg'}
    boxShadow={frameFrom ? { base: 'none', md: 'surface' } : 'surface'}
    minW={0}
    {...props}
  />
)

/**
 * A short link inside a sentence with a 48 px tall hit box: an inline block (the line height, 24 px) padded 12 px above
 * and below, and pulled back by the same negative margins, so the line around it keeps its height. For one or two
 * words: an inline block never breaks across lines. (A link inside a checkbox label stays inline: the label is the
 * target.)
 */
export const INLINE_TARGET = { display: 'inline-block', paddingBlock: '0.75rem', marginBlock: '-0.75rem' } as const

/** An in-text link to another page (underlined, so it reads as a link inside a sentence), 48 px tall. */
export const TextLink = ({ to, children }: { to: string; children: ReactNode }) => (
  <CLink asChild textDecoration='underline' textUnderlineOffset='2px' fontWeight='medium' css={INLINE_TARGET}>
    <RouterLink to={to}>{children}</RouterLink>
  </CLink>
)

/** What the help sheet shows: `{}` the sheet with nothing expanded, `{ faq }` that FAQ entry opened. */
type HelpTarget = { faq?: FaqId }

/** Opens the help sheet from anywhere inside a MemberLayout, on one FAQ entry or on none (`open()`). */
const HelpContext = createContext<{ open: (target?: { faq: FaqId }) => void }>({ open: () => {} })
export const useHelp = () => useContext(HelpContext)

/**
 * Provides the help sheet to everything inside it. `children` is a render function so the page can put its own
 * "Help" button anywhere: it receives `open`, which opens the sheet with no entry expanded.
 */
export function HelpProvider({
  flow = false,
  children,
}: {
  /** True on the registration steps after the email is confirmed: the sheet offers to delete the registration. */
  flow?: boolean
  children: (open: () => void) => ReactNode
}) {
  const [view, setView] = useState<HelpTarget | null>(null)
  const help = useMemo(() => ({ open: (target?: { faq: FaqId }) => setView(target ?? {}) }), [])
  return (
    <HelpContext.Provider value={help}>
      {children(() => setView({}))}
      <HelpSheet view={view} onViewChange={setView} flow={flow} />
    </HelpContext.Provider>
  )
}

/**
 * "Already registered? Log in": one link to /login. The question is muted and, under `sm`, hidden visually; the
 * accessible name is always the full sentence.
 */
export function AlreadyRegisteredLink({ button = false }: { button?: boolean }) {
  const { t } = useT()
  const label = `${t('common.alreadyRegistered')} ${t('common.logIn')}`
  const content = (
    <>
      <Text as='span' color='fg.muted' hideBelow='sm' aria-hidden>
        {t('common.alreadyRegistered')}
      </Text>
      <Text as='span' fontWeight='medium' aria-hidden>
        {t('common.logIn')}
      </Text>
    </>
  )
  if (button)
    return (
      <Button asChild variant='ghost' flexShrink={0} gap={1.5} px={3}>
        <RouterLink to='/login' aria-label={label}>
          {content}
        </RouterLink>
      </Button>
    )
  return (
    <CLink
      asChild
      display='inline-flex'
      alignItems='center'
      gap={1.5}
      minH='12'
      minW='12'
      textDecoration='none'
      _hover={{ '& > span:last-of-type': { textDecoration: 'underline' } }}
    >
      <RouterLink to='/login' aria-label={label}>
        {content}
      </RouterLink>
    </CLink>
  )
}

/**
 * "Log in" top right while nobody is logged in; "My member page" once the member page has loaded the member (read
 * from the cache only: this never asks the server). Nothing on the log in page itself or on the member page.
 */
function HeaderAccount() {
  const { t } = useT()
  const { pathname } = useLocation()
  const me = useQuery({ queryKey: keys.me, queryFn: () => api('memberMe'), enabled: false })
  if (pathname === '/login') return <Box />
  // Under `sm` the button shows its icon only (the name stays for screen readers), so the organization's name keeps
  // its room (mobile review, ruling M4).
  if (me.data)
    return (
      <Button asChild variant='ghost' flexShrink={0} minW='12' px={3}>
        <RouterLink to='/me'>
          <LuUser aria-hidden />
          <chakra.span css={{ '@media screen and (max-width: 29.99rem)': visuallyHidden }}>
            {t('common.myMemberPage')}
          </chakra.span>
        </RouterLink>
      </Button>
    )
  return <AlreadyRegisteredLink button />
}

/** `html[data-app-shell]` while a MemberLayout is mounted (the global CSS stops the document scrolling on a phone). */
let shellUsers = 0
function useAppShellFlag() {
  useLayoutEffect(() => {
    shellUsers += 1
    document.documentElement.dataset.appShell = ''
    return () => {
      shellUsers -= 1
      if (shellUsers === 0) delete document.documentElement.dataset.appShell
    }
  }, [])
}

/** The path the shell showed last, across MemberLayout instances (each page mounts its own). */
let lastPath: string | null = null

/**
 * A new screen (a navigation, not the first page load): the page fades and slides in, the scroller is back at the top
 * and the page's `h1` takes the focus, so a screen reader starts reading the new screen (design spec §7, §8 item 3).
 * The focus never moves when the member has already put it somewhere (a field, a button). The two halves of a step
 * (`?part=`) are two screens.
 */
function useScreenChange(scroller: RefObject<HTMLElement | null>, page: RefObject<HTMLElement | null>) {
  const { pathname, search } = useLocation()
  const navigation = useNavigationType()
  const part = new URLSearchParams(search).get('part')
  const screen = part ? `${pathname}?part=${part}` : pathname
  useEffect(() => {
    const before = lastPath
    lastPath = screen
    if (before === null || before === screen) return
    // A half-step link without its choice replaced by the first half of the same step: still the page that loaded.
    if (navigation === 'REPLACE' && before.split('?')[0] === pathname) return
    scroller.current?.scrollTo?.({ top: 0 })
    const el = page.current
    if (!el) return
    const motion = !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (motion && typeof el.animate === 'function')
      el.animate(
        [
          { opacity: 0, transform: 'translateY(8px)' },
          { opacity: 1, transform: 'none' },
        ],
        { duration: 160, easing: 'ease-out' }
      )
    const focusHeading = () => {
      const h1 = el.querySelector('h1')
      if (!h1) return false
      const active = document.activeElement
      if (!active || active === document.body) {
        h1.tabIndex = -1
        h1.focus({ preventScroll: true })
      }
      return true
    }
    if (focusHeading()) return
    // A lazy page or a skeleton first: wait (a little) for the heading.
    const watch = new MutationObserver(() => {
      if (focusHeading()) watch.disconnect()
    })
    watch.observe(el, { childList: true, subtree: true })
    const stop = window.setTimeout(() => watch.disconnect(), 5000)
    return () => {
      watch.disconnect()
      window.clearTimeout(stop)
    }
  }, [screen, navigation, pathname, scroller, page])
}

/** The action area docks (a top hairline and an upward shadow) while there is more of the screen under it. */
function useDockShadow(scroller: RefObject<HTMLElement | null>, slot: HTMLElement | null) {
  useEffect(() => {
    const el = scroller.current
    if (!el || !slot) return
    let frame = 0
    const measure = () => {
      frame = 0
      slot.toggleAttribute('data-floating', el.scrollTop + el.clientHeight < el.scrollHeight - 1)
    }
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure)
    }
    measure()
    el.addEventListener('scroll', schedule, { passive: true })
    const sizes = new ResizeObserver(schedule)
    sizes.observe(el)
    for (const child of el.children) sizes.observe(child)
    return () => {
      el.removeEventListener('scroll', schedule)
      sizes.disconnect()
      if (frame) cancelAnimationFrame(frame)
    }
  }, [scroller, slot])
}

/** "Help" in the top bar: the icon alone under 480 px (the name stays for screen readers). */
function HelpButton({ onClick }: { onClick: () => void }) {
  const { t } = useT()
  return (
    <Button variant='ghost' flexShrink={0} minW='12' px={{ base: 0, sm: 3 }} onClick={onClick}>
      <LuCircleHelp aria-hidden />
      <chakra.span css={{ '@media screen and (max-width: 29.99rem)': visuallyHidden }}>{t('common.help')}</chakra.span>
    </Button>
  )
}

/**
 * The app shell of every member screen (design spec 2026-10-30 §2): a top bar (the organization's mark; "Log in" or
 * "My member page", Help and the theme), the screen, the mini footer, and the bottom action area.
 *
 * On a phone (`COMPACT`) the shell is exactly the screen's height and works like a native app: the top bar stays, the
 * screen scrolls inside `[data-app-scroller]` (the document itself never scrolls), and the action area under the
 * scroller holds the screen's main button (ActionBar renders into it), always in view and above the keyboard (on iOS
 * through `--app-vh` and `--app-top`, measured from `visualViewport` (useKeyboardInset)), clear of the home indicator. On a tablet or a computer
 * (`ROOMY`) the page flows normally: one centred, phone-width card holds the screen and its main button; `wide` pages
 * (the start and log-in split cards, the capture pages) lay out their own sections. The help sheet opens over the
 * page, which is never lost.
 */
export function MemberLayout({
  children,
  backTo,
  backHistory = false,
  flow = false,
  wide = false,
  account = true,
}: {
  children: ReactNode
  /** The header's "Log in" / "My member page" button (off on the confirmation after the data is erased). */
  account?: boolean
  /** Where "Back" goes; no link when there is nowhere sensible to go back to. */
  backTo?: string
  /**
   * Back goes one step in the browser history when there is one (legal pages, the second screen of a split step: so
   * the phone's Back does not return to it); `backTo` stays the fallback.
   */
  backHistory?: boolean
  /** True on the registration steps after the email is confirmed: the help sheet offers the continue and delete links. */
  flow?: boolean
  /** The page lays out its own sections (no card around it). */
  wide?: boolean
}) {
  const [reported, setReported] = useState<StepKey | null>(null)
  const [slot, setSlot] = useState<HTMLElement | null>(null)
  const root = useRef<HTMLDivElement>(null)
  const scroller = useRef<HTMLDivElement>(null)
  const page = useRef<HTMLDivElement>(null)
  useAppShellFlag()
  useKeyboardInset(root, scroller)
  useScreenChange(scroller, page)
  useDockShadow(scroller, slot)
  // A registration step shows Back inside its step header (FlowStepper); other pages get a Back row of their own.
  const back = backTo && reported === null ? <BackLink to={backTo} history={backHistory} /> : null
  return (
    <HelpProvider flow={flow}>
      {(openHelp) => (
        <StepContext.Provider value={setReported}>
          <BackContext.Provider value={backTo ? { to: backTo, history: backHistory } : undefined}>
            <ActionBarSlot.Provider value={slot}>
              <Flex
                ref={root}
                data-app-shell=''
                direction='column'
                bg='bg.canvas'
                position='relative'
                css={{
                  minHeight: '100dvh',
                  [COMPACT]: {
                    height: 'var(--app-vh, 100dvh)',
                    minHeight: 0,
                    transform: 'translateY(var(--app-top, 0px))',
                  },
                }}
              >
                <SkipLink />
                <Box
                  as='header'
                  flexShrink={0}
                  bg='bg.chrome'
                  borderBottom='1px solid'
                  borderColor='border.chrome'
                  css={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
                >
                  <SiteContainer>
                    <Flex
                      justify='space-between'
                      align='center'
                      gap={2}
                      css={{ height: '12', [ROOMY]: { height: '14' } }}
                    >
                      <OrgMark markOnlyWhenNarrow />
                      <Flex align='center' gap={1} flexShrink={0}>
                        {account && <HeaderAccount />}
                        <HelpButton onClick={openHelp} />
                        <ThemeControl />
                      </Flex>
                    </Flex>
                  </SiteContainer>
                </Box>
                <Flex
                  ref={scroller}
                  data-app-scroller=''
                  direction='column'
                  flex='1'
                  css={{
                    [COMPACT]: {
                      minHeight: 0,
                      overflowY: 'auto',
                      overscrollBehavior: 'contain',
                      WebkitOverflowScrolling: 'touch',
                      scrollPaddingBottom: '1rem',
                    },
                  }}
                >
                  <Box
                    as='main'
                    id='main'
                    flex='1'
                    w='full'
                    mx='auto'
                    css={{
                      padding: '0.5rem 1rem',
                      maxWidth: wide ? '80rem' : '32rem',
                      [ROOMY]: { paddingInline: '1.5rem', paddingBlock: '2.5rem' },
                      [ROOMY_LOW]: { paddingBlock: '1.5rem' },
                    }}
                  >
                    <Stack
                      ref={page}
                      minW={0}
                      css={
                        wide
                          ? { gap: '6' }
                          : {
                              gap: '3',
                              // The page card on a tablet or a computer: a content surface raised off the canvas
                              // (R113), phone-width. On a phone the screen sits on the canvas, its fields filled white.
                              [ROOMY]: {
                                gap: '6',
                                padding: '8',
                                borderWidth: '1px',
                                borderColor: 'border',
                                borderRadius: 'lg',
                                background: 'bg',
                                boxShadow: 'surface',
                              },
                            }
                      }
                    >
                      {back && <Box>{back}</Box>}
                      {children}
                    </Stack>
                  </Box>
                  <Box css={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)', [ROOMY]: { paddingBottom: '2' } }}>
                    <MiniFooter />
                  </Box>
                </Flex>
                {/* The action area (phones): ActionBar renders the screen's main button here. Empty, it takes no room. */}
                <Box
                  ref={setSlot}
                  data-action-area=''
                  flexShrink={0}
                  bg='bg.canvas'
                  borderTop='1px solid'
                  borderColor='transparent'
                  css={{
                    padding: '0.75rem 1rem calc(0.75rem + env(safe-area-inset-bottom, 0px))',
                    '&:empty': { display: 'none' },
                    '& > *': { maxWidth: '30rem', marginInline: 'auto' },
                    '&[data-floating]': { borderColor: 'border.chrome', boxShadow: 'dockBottom' },
                  }}
                />
              </Flex>
            </ActionBarSlot.Provider>
          </BackContext.Provider>
        </StepContext.Provider>
      )}
    </HelpProvider>
  )
}
